import { WaterfallAdjustmentsModal } from "features/waterfall/review/WaterfallAdjustmentsModal";
import { WaterfallReviewPageContext } from "features/waterfall/review/WaterfallReviewPage";
import { WaterfallReviewTableBoxTabs } from "features/waterfall/review/WaterfallReviewTableBoxTabs";
import {
  useWaterfallBreakdownRetrieveSuspense,
  useWaterfallInvestorLevelBreakdownTableDataRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import React, { useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ClickableElementGenericLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { DataTableCellLink } from "shared/components/table/data/DataTableCellLink";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const WaterfallReviewInvestorPage: React.FC = () => {
  const { fundFamilyId, fundFamilyEventId, fundFamilySubEventId } =
    useRequiredStringParams();

  const navigate = useNavigate();
  const fundLevelPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId })
    .waterfall({})
    .review({
      fundFamilySubEventId,
    }).$;
  const navigateToFundLevel = useCallback(
    () => navigate(fundLevelPath),
    [fundLevelPath, navigate],
  );

  const { isEqualizedView, waterfallAdjustmentState, fundId } =
    useOutletContext<WaterfallReviewPageContext>();

  const [adjustmentModalIsOpen, setAdjustmentModalIsOpen] =
    waterfallAdjustmentState;

  const { data: investorTableData } =
    useWaterfallInvestorLevelBreakdownTableDataRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
      fundFamilySubEventId,
      fundId,
      {
        is_equalized_view: isEqualizedView || undefined,
      },
    );

  const { data: breakdownTableData } = useWaterfallBreakdownRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
    fundFamilySubEventId,
    fundId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
  );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const baseRoute = rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId })
        .waterfall({})
        .review({ fundFamilySubEventId });

      const renderedValue = defaultDataTableRenderCellValue(
        props,
        fieldDisabled,
        params,
      );
      const { cell } = props;

      if (cell.metadata?.type == "investor_entity") {
        const linkInfo: ClickableElementGenericLinkInfo = {
          type: "generic",
          variant: "tableCellAction",
          to: baseRoute
            .breakdown({
              fundEntityId: fundId,
              investorEntityId: cell.metadata.investor_entity_id,
            })
            .investor({}).$,
        };
        return (
          <DataTableCellLink
            tableContext={props.tableContext}
            columnContext={props.columnContext}
            row={props.row}
            linkInfo={linkInfo}
            onButtonClick={undefined}
          >
            {renderedValue}
          </DataTableCellLink>
        );
      }
      return renderedValue;
    },
    [fundFamilyId, fundId, fundFamilySubEventId, fundFamilyEventId],
  );

  return (
    <PageTabBody>
      <TableBox isFullWidth={true}>
        <TableBoxHeader>
          <WaterfallReviewTableBoxTabs />
          <DataTableBoxActions />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            renderCellValue={renderCellValue}
            data={investorTableData}
          />
        </TableBoxBody>
      </TableBox>
      {adjustmentModalIsOpen && (
        <WaterfallAdjustmentsModal
          distributionData={breakdownTableData}
          isOpen={adjustmentModalIsOpen}
          onSuccess={() => {
            setAdjustmentModalIsOpen(false);
            navigateToFundLevel();
          }}
          onCloseComplete={() => {
            setAdjustmentModalIsOpen(false);
          }}
          fundId={fundId}
        />
      )}
    </PageTabBody>
  );
};
